import { Dialog, Transition } from "@headlessui/react";
import algosdk from "algosdk";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { convertBase64 } from "helpers/functions";
import useTheme from "hooks/useTheme";

const NewContactModal = ({ isOpen, setIsOpen, title, action, formData, setFormData }) => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor } = useTheme();

	const [isAccountValid, setIsAccountValid] = useState(true);
	const hiddenImageFileInput = useRef();

	useEffect(() => {
		setFormData(false);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	useEffect(() => {
		if (formData?.["account-address"]) {
			checkIfValidAddress(formData["account-address"]);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formData]);

	const checkIfValidAddress = (accountID) => {
		try {
			const isValid = algosdk.isValidAddress(accountID);

			setIsAccountValid(isValid ? isValid : false);
		} catch (error) {
			console.error(error);
		}
	};

	const handleFileRead = async (e) => {
		const file = e.target.files[0];
		const base64 = await convertBase64(file);

		setFormData({ ...formData, "profile-image": base64 });
	};

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-10" onClose={() => setIsOpen(false)}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className={`fixed inset-0 ${themeColor?.bg} bg-opacity-30`} />
				</Transition.Child>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<Dialog.Panel className="w-full items-center justify-center max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
								<div className="flex flex-col w-full space-x-4 items-center space-y-6">
									<div className="flex-col w-full space-y-3">
										<Dialog.Title
											as="h3"
											className="flex flex-col w-full items-center justify-center font-medium leading-6 text-gray-900"
										>
											<p className={`text-lg ${themeColor?.text}`}>{title}</p>
											<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.youCanEditYourContact}</p>
										</Dialog.Title>
									</div>
									<div className="w-full flex flex-col space-y-4">
										<div>
											<label htmlFor="friendly-name" className={`text-sm ${themeColor?.text}`}>
												{appLanguage?.friendlyName}
											</label>
											<input
												name="friendly-name"
												className="flex w-full text-sm border px-3 py-2 rounded-lg focus:outline-none"
												placeholder={appLanguage?.friendlyName}
												onChange={(e) => handleChange(e)}
											/>
										</div>
										<div className="flex w-full items-center justify-start space-x-3">
											<button
												className={`w-fit text-white ${themeColor?.bg} rounded-lg px-3 py-2`}
												onClick={() => hiddenImageFileInput.current.click()}
											>
												{formData?.["profile-image"] ? "Change Avatar" : "Add an Avatar"}
											</button>

											<input
												style={{ display: "none" }}
												ref={hiddenImageFileInput}
												type="file"
												accept="image/x-png,image/jpeg"
												onChange={(e) => handleFileRead(e)}
											/>

											{formData?.["profile-image"] ? (
												<img
													className={`w-10 h-10 object-contain object-center`}
													src={formData["profile-image"]}
													alt="temp"
													loading="lazy"
												/>
											) : (
												<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.noFileSelected}</p>
											)}
										</div>
										<div>
											<label htmlFor="account-address" className={`text-sm ${themeColor?.text}`}>
												{appLanguage?.accountNumber}
											</label>
											<input
												name="account-address"
												className="flex w-full text-xs border px-3 py-2 rounded-lg focus:outline-none"
												placeholder={appLanguage?.accountAddress}
												onChange={(e) => handleChange(e)}
											/>
											{!isAccountValid && (
												<p className="text-xs italic text-candy-red">{appLanguage?.invalidAccount}</p>
											)}
										</div>
									</div>
								</div>

								<div className="w-full flex mt-4 space-x-3 justify-end">
									<button
										type="button"
										className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white ${themeColor.bg} disabled:bg-gray-200 disabled:${themeColor?.text}`}
										onClick={() => {
											dispatch(action);
										}}
										disabled={
											formData?.["friendly-name"] && formData?.["account-address"] && isAccountValid
												? false
												: true
										}
									>
										{appLanguage?.createContact}
									</button>
									<button
										type="button"
										className={`inline-flex justify-center rounded-md border px-4 py-2 text-sm font-medium ${themeColor?.text}`}
										onClick={() => setIsOpen(false)}
									>
										{appLanguage?.cancel}
									</button>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
};

export default NewContactModal;
